import React, { useState } from 'react';
import Sidebar from 'components/Sidebar';
import MobileHeader from 'components/MobileHeader';
import SearchOverlay from 'components/SearchOverlay';
import LoadingOverlay from 'components/LoadingOverlay';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAgent } from 'api/agents';
import Container from 'components/Container';
import { exportToSpreadsheet } from 'helpers/excel';
import Button from 'components/Button';
import { getConversation, getConversationMessages } from 'api/conversations';
import { differenceInMinutes, format, getMonth, getWeek } from 'date-fns';

export default function Agent() {

    const [sidebar, setSidebar] = useState(false);
    const [search, setSearch] = useState(false);
    const { agentId, conversationId } = useParams();

    const { data: agent, isLoading } = useQuery({
        queryKey: ['agents', agentId],
        queryFn: async () => await getAgent({
            id: agentId
        }),
    });

    const { data: conversation, isLoading: isLoadingConversation, refetch } = useQuery({
        queryKey: ['conversations', conversationId],
        queryFn: async () => await getConversation({id: conversationId}),
        enabled: !!conversationId
    })

    const { data: messages, isLoading: loadingMessages } = useQuery({
        queryKey: ['conversations', conversationId, 'messages'],
        queryFn: async () => {
            const data = await getConversationMessages({id: conversationId});
            if(conversation?.status === "PENDING") {
                refetch()
            }
            return data;
        },
        refetchInterval: conversation?.status === "PENDING" ? 5000 : false,
        enabled: !!conversationId
    });

    const result = messages?.reduce((acc, msg) => {
        if(msg.data) {
            return [
                ...acc,
                ...msg.data?.agent || []
            ]
        }
        return acc;
    }, []);

    const onDownloadExcelHandler = () => {
        // DATA - MESE - SETTIMANA - (CLIENTE) - OPERATORE - ORARIO INGRESSO USCITA - MINUTI TOTALI - (STATO) - (COMMESSA) - ATTIVITA'
        let content = result.slice(1);
        const header = [
            'DATA',
            'MESE',
            'SETTIMANA',
            'CLIENTE',
            'OPERATORE',
            'ORARI',
            'MINUTI TOTALI',
            'STATO',
            'COMMESSA',
            "ATTIVITA'"
        ]
        const DATA = result[0].indexOf('data');
        const OPERATORE = result[0].indexOf('nomeDipendente');
        const ATTIVITA = result[0].indexOf('lavoroFatto');
        const ORAINIZIO = result[0].indexOf('oraInizio');
        const ORAFINE = result[0].indexOf('oraFine');

        content = content.map(row => {
            try {
                let date;
                try {
                    date = row[DATA] && format(new Date(row[DATA]), 'dd/MM/yyyy');
                } catch (error) {
                    date = "Illeggibile";
                }
                const orari = `${row[ORAINIZIO]} - ${row[ORAFINE]}`;
                const [ oraInizioHour, oraInizioMinute ] = row[ORAINIZIO].split(':').map(Number);
                const [ oraFineHour, oraFineMinute ] = row[ORAFINE].split(':').map(Number);
                const startDate = new Date(1970, 0, 1, oraInizioHour, oraInizioMinute || 0);
                const endDate = new Date(1970, 0, 1, oraFineHour, oraFineMinute || 0);
                const minuti = differenceInMinutes(endDate, startDate);
                const mese = getMonth(new Date(row[DATA])) + 1;
                const settimana = getWeek(new Date(row[DATA]));
                return [
                    date,
                    mese,
                    settimana,
                    '',
                    row[OPERATORE],
                    orari,
                    minuti,
                    '',
                    '',
                    row[ATTIVITA]
                ];
            } catch (error) {
                return [
                    "Errore"
                ];
            }
        });
        return exportToSpreadsheet([
            header,
            ...content
        ], `rossato-orari-${new Date().toISOString()}.xlsx`);
    };

    const headerProdotti = Object.keys(result?.[0]?.prodotti?.[0] || []);
    console.log(result);

    return (
        <>
            <Sidebar
                visible={sidebar}
                setVisible={setSidebar}
            />

            <SearchOverlay
                visible={search}
                setVisible={setSearch}
            />

            <MobileHeader
                sidebar={sidebar}
                setSidebar={setSidebar}
                search={search}
                setSearch={setSearch}
            />

            <LoadingOverlay isLoading={isLoading || isLoadingConversation || loadingMessages} />

            <div className={`fixed top-0 w-full bottom-0 transition-all duration-300 ease-in-out lg:pl-[320px] overflow-auto pt-20 md:pt-14 pb-20 ${sidebar ? 'left-[80vw]' : 'left-0'}`}>
                <Container>
                    <div className='w-full flex items-center gap-5'>
                        <div className='w-12 min-w-12 md:w-24 h-24 rounded-full bg-center bg-cover' style={{ backgroundImage: `url(${agent?.image || '/images/agent-avatar.jpg'})` }}></div>
                        <div>
                            <h1 className='text-xl md:text-3xl font-medium text-black pb-1'>{agent?.name}</h1>
                            <p className='text-muted'>{agent?.description}</p>
                        </div>
                    </div>

                    {result &&
                        <div className='w-full pt-10'>
                            <div className='flex justify-between items-center'>
                                <h3 className='text-black text-xl'>Risultati</h3>
                                <div className='flex items-center gap-4'>
                                    <Button to={`/agents/${agent?.slug}/${agentId}?conversation=${conversationId}`} >
                                        Aggiungi elementi
                                    </Button>
                                    <Button  component="button" onClick={onDownloadExcelHandler} >
                                        Scarica Excel
                                    </Button>
                                </div>
                            </div>
                            <div className='overflow-x-auto mt-2 rounded-sm'>
                                {result.map((doc, index) => (
                                    // Report dei dati estratti del documento, prima dati documento come numero e data, poi a seguire i dati del mittente, sotto ancora la tabella con i prodotti
                                    <div className='min-w-[800px] border border-light mb-10' key={index}>
                                        <div className='flex justify-between p-2 border-b border-light font-medium bg-verylight'>
                                            <div>
                                                <p className=''>{doc.mittente?.nome}</p>
                                                {doc.mittente?.piva !== "-" && <p className='text-sm text-muted'>p.iva {doc.mittente?.piva}</p>}
                                                <p className='text-sm text-muted'>{doc.mittente?.indirizzo}</p>
                                            </div>
                                            <p className=''>#{doc.numero} del {doc.data}</p>
                                        </div>
                                        <div className='p-2'>
                                            <table className='w-full'>
                                                <thead>
                                                    <th className={`border-b border-light flex`}>
                                                        {headerProdotti.map((field, fieldIndex) => (
                                                            <td key={fieldIndex} className="flex-1 p-2 text-sm text-left">
                                                                {field}
                                                            </td>
                                                        ))}
                                                    </th>
                                                </thead>
                                                <tbody>
                                                    {doc.prodotti.map((product, productIndex) => (
                                                        <tr className={`border-b border-light flex`} key={productIndex}>
                                                            {headerProdotti.map((field, fieldIndex) => (
                                                                <td key={fieldIndex} className="flex-1 p-2 text-sm">
                                                                    {product[field]}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    }
                </Container>
            </div>
        </>
    )
}
